// screens/MatchSchedule.js - Visualizzazione del calendario degli incontri

import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { View, Text, FlatList, StyleSheet, TouchableOpacity, Modal, ScrollView } from 'react-native';
import { loadTeams, loadMatches } from '../utils/storage';

import InvertiLatiButton from '../components/InvertiLatiButton';
import ScrollToTopButton from '../components/ScrollToTopButton';
import NumpadModal from '../components/NumpadModal';
import { ScheduleIcon } from '../components/FencingIcons';

const theme = {
  primary: '#1976d2',
  secondary: '#388e3c',
  accent: '#ffe082',
  danger: '#d32f2f',
  bg: '#f7fafd',
  card: '#fff',
  border: '#e0e7ef',
  shadow: '0 2px 16px #0002',
};

// Colori per i gironi
const poolColors = [
  '#1976d2', '#388e3c', '#f57c00', '#7b1fa2', '#c2185b',
  '#0097a7', '#5d4037', '#455a64', '#e64a19', '#afb42b'
];



function getMatchStatus(match) {
  if (match.completed) return { label: 'Completato', color: theme.secondary };
  if ((match.scoreA || match.scoreB) > 0) return { label: 'In corso', color: theme.accent };
  return { label: '', color: theme.primary };
}

function handleQuickScore(matches, setMatches, idx, team, delta) {
  const newMatches = [...matches];
  const m = { ...newMatches[idx] };
  const maxScore = parseInt(m.stoccateTotali || 45, 10);

  if (team === 'A') {
    if (delta > 0) m.scoreA = Math.min(maxScore, (m.scoreA || 0) + delta);
    else m.scoreA = Math.max(0, (m.scoreA || 0) + delta);

    // Se il punteggio A raggiunge il massimo, il match è completato
    if (m.scoreA >= maxScore) {
      m.completed = true;
    } else {
      // Se nessun punteggio è al massimo, il match è in corso
      m.completed = m.scoreB >= maxScore;
    }
  } else {
    if (delta > 0) m.scoreB = Math.min(maxScore, (m.scoreB || 0) + delta);
    else m.scoreB = Math.max(0, (m.scoreB || 0) + delta);

    // Se il punteggio B raggiunge il massimo, il match è completato
    if (m.scoreB >= maxScore) {
      m.completed = true;
    } else {
      // Se nessun punteggio è al massimo, il match è in corso
      m.completed = m.scoreA >= maxScore;
    }
  }
  newMatches[idx] = m;
  setMatches(newMatches);
  localStorage.setItem('matches', JSON.stringify(newMatches));
}

// getBadgeMinWidth rimossa (inutilizzata)

const sanitizeScore = (score) => {
  const num = Number(score);
  return Number.isFinite(num) ? Math.max(0, num) : 0;
};

const MatchSchedule = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // Carica SEMPRE i dati aggiornati da localStorage all'apertura
  const [teams, setTeams] = useState([]);
  const [editableMatches, setMatches] = useState([]);
  const [competitionType, setCompetitionType] = useState('team'); // Default: team
  const [selectedMatchIndex, setSelectedMatchIndex] = useState(null);
  const [activeTeamInput, setActiveTeamInput] = useState(null); // 'A' o 'B'
  const [showNumpad, setShowNumpad] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [isInputReplaceMode, setIsInputReplaceMode] = useState(true);
  // Impostazioni di stoccate caricate da localStorage
  const [stoccateGirone, setStoccateGirone] = useState(5);
  const [stoccateElimDiretta, setStoccateElimDiretta] = useState(15);
  const [showIncomplete, setShowIncomplete] = useState(() => {
    const savedShowIncomplete = localStorage.getItem('showIncomplete');
    return savedShowIncomplete
      ? JSON.parse(savedShowIncomplete)
      : false;
  });
  const [teamsLoaded, setTeamsLoaded] = useState(false);
  const [matchesLoaded, setMatchesLoaded] = useState(false);
  const [filteredPool, setFilteredPool] = useState(null); // null = mostra tutti i gironi
  // Stato per memorizzare gli stati di inversione lati per ciascun match
  const [matchInversions, setMatchInversions] = useState({});
  // Stati per il filtro atleta
  const [selectedAthlete, setSelectedAthlete] = useState(null);
  const [showAthleteModal, setShowAthleteModal] = useState(false);
  const [athletesList, setAthletesList] = useState([]);

  // Funzione per recuperare lo stato di inversione per un match specifico
  const getMatchInversion = (matchId) => {
    // IMPORTANTE: Converti sempre matchId a stringa per coerenza con le chiavi localStorage
    const matchKey = String(matchId);
    const isInverted = matchId !== undefined && matchInversions[matchKey] === true;
    // Log solo quando viene effettivamente chiamato per un matchId specifico
    if (matchId !== undefined) {
      console.log(`[DEBUG INVERSIONE] getMatchInversion(${matchKey}) => ${isInverted}`);
    }
    return isInverted;
  };

  // Funzione per toggleInvertiLati specifica per un match
  const toggleMatchInversion = (matchId) => {
    if (matchId === undefined) return;

    // Carica il valore attuale prima di modificarlo
    const currentValue = getMatchInversion(matchId);
    const newValue = !currentValue;

    console.log(`[MatchSchedule] TOGGLE INVERSIONE per match ${matchId}: da ${currentValue} a ${newValue}`);

    try {
      // IMPORTANTE: Usa sempre String(matchId) come chiave per coerenza
      const matchKey = String(matchId);

      // Persisti prima in localStorage per garantire coerenza
      const preferences = JSON.parse(localStorage.getItem('match_inversione_lati') || '{}');
      preferences[matchKey] = newValue;
      localStorage.setItem('match_inversione_lati', JSON.stringify(preferences));

      // QUINDI aggiorna lo stato locale
      const newInversions = { ...matchInversions };
      newInversions[matchKey] = newValue;
      setMatchInversions(newInversions);

      console.log('[MatchSchedule] Nuovo stato inversioni aggiornato:', newInversions);

      // Invia un evento personalizzato per notificare altri componenti
      window.dispatchEvent(new CustomEvent('inversioneLatiChanged', {
        detail: { matchId: matchKey, inverted: newValue }
      }));

      // Forza un re-render DIRETTO dell'interfaccia utente
      // Crea una copia diretta dell'array per forzare React a ricalcolare la vista
      const updatedMatches = [...editableMatches];
      setMatches(updatedMatches);

      // Ripristina esplicitamente lo stato di inversione anche dopo l'aggiornamento
      // per garantire che i componenti figli vengano aggiornati
      setTimeout(() => {
        setMatchInversions({ ...newInversions });
      }, 50);

    } catch (e) {
      console.error('Errore nel salvataggio preferenze inversione:', e);
    }
  };

  // Funzione per caricare le preferenze di inversione
  const loadInversionePreferences = useCallback(() => {
    try {
      const preferences = JSON.parse(localStorage.getItem('match_inversione_lati') || '{}');

      // Verifica che sia effettivamente un oggetto
      if (typeof preferences !== 'object' || preferences === null) {
        console.error('[MatchSchedule] Errore: preferenze non valide nel localStorage');
        return;
      }

      // Non filtrare, carica TUTTI i valori per mantenere la coerenza
      const allInversions = {};
      Object.keys(preferences).forEach(key => {
        allInversions[key] = !!preferences[key]; // Converti esplicitamente in booleano
      });

      console.log('[MatchSchedule] Caricato stato inversioni completo:', allInversions);

      // Forza un nuovo oggetto per aggiornare lo stato React
      setMatchInversions({ ...allInversions });

      // Forzare un re-render anche della lista dopo aver caricato le preferenze
      setMatches(matches => [...matches]);
    } catch (e) {
      console.error('Errore nel recupero preferenze inversione:', e);
    }
  }, []); // Dipendenze per loadInversionePreferences


  // Carica le preferenze di inversione all'inizio e quando cambia lo storage
  useEffect(() => {
    // Carica all'inizio
    loadInversionePreferences();

    // Ascolta eventi di storage e custom
    const handleStorageChange = (event) => {
      if (event.key === 'match_inversione_lati') {
        console.log('[MatchSchedule] Rilevato cambiamento nel localStorage');
        loadInversionePreferences();
      }
    };


    const handleInversioneChanged = (event) => {
      console.log('[MatchSchedule] Ricevuto evento di inversione lati:', event.detail);

      // SEMPRE ricarica da localStorage per evitare problemi di stale closure
      // Questo garantisce che abbiamo sempre lo stato più aggiornato
      loadInversionePreferences();

      // Forza re-render della lista
      setMatches(matches => [...matches]);

      if (event.detail && event.detail.matchId !== undefined) {
        console.log(`[MatchSchedule] Evento per match ${event.detail.matchId}: ${event.detail.inverted}`);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('inversioneLatiChanged', handleInversioneChanged);

    // Carica anche quando questa schermata diventa attiva
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        console.log('[MatchSchedule] Pagina diventata visibile, aggiorno le inversioni');
        loadInversionePreferences();
        setMatches(matches => [...matches]);
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Ascolta anche popstate per quando si naviga indietro con React Router
    const handlePopState = () => {
      console.log('[MatchSchedule] Navigazione indietro rilevata, aggiorno le inversioni');
      loadInversionePreferences();
      setMatches(matches => [...matches]);
    };
    window.addEventListener('popstate', handlePopState);

    // Ascolta anche il focus della finestra per catturare ritorno da altre viste
    const handleWindowFocus = () => {
      console.log('[MatchSchedule] Finestra in focus, aggiorno le inversioni');
      loadInversionePreferences();
      setMatches(matches => [...matches]);
    };
    window.addEventListener('focus', handleWindowFocus);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('inversioneLatiChanged', handleInversioneChanged);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('popstate', handlePopState);
      window.removeEventListener('focus', handleWindowFocus);
    };
  }, [loadInversionePreferences]);

  // Ricarica le inversioni ogni volta che si naviga su questa pagina
  // location.key cambia ad ogni navigazione, garantendo il reload
  useEffect(() => {
    console.log('[MatchSchedule] Navigazione rilevata (location.key changed), ricarico inversioni');
    loadInversionePreferences();
    setMatches(matches => [...matches]);
  }, [location.key, loadInversionePreferences]);

  useEffect(() => {
    // Salva lo stato del filtro nel localStorage
    localStorage.setItem('showIncomplete', JSON.stringify(showIncomplete));
  }, [showIncomplete]);

  // --- MIGRAZIONE NOMI SQUADRE E POTENZIAMENTO IDENTIFICAZIONE ATLETI/MATCH ---
  const migraNomiSquadreMixed = useCallback((matches, teams) => {
    console.log('[MatchSchedule] Migrazione nomi match basata su nomi tiratori...');

    // Costruiamo una mappa degli atleti per nome per lookup veloce
    const atletiByName = {};
    const atletiByIndex = {};

    if (teams && teams.length > 0) {
      teams.forEach((team, index) => {
        if (team.members && team.members.length > 0) {
          const memberName = team.members[0];
          if (memberName && typeof memberName === 'string') {
            atletiByName[memberName] = { team, index };
            atletiByIndex[index] = { memberName, team };
            console.log(`[MatchSchedule] Mappato atleta: ${memberName} => indice ${index}`);
          }
        }
      });
    }

    // Migliora tutti i match con informazioni stabili
    return matches.map(match => {
      // Copia profonda per non modificare l'originale
      const nuovoMatch = { ...match };

      // Estrai i dati da indici più stabili (i nomi dei membri)
      const teamAIndex = typeof nuovoMatch.teamA === 'number' ? nuovoMatch.teamA : -1;
      const teamBIndex = typeof nuovoMatch.teamB === 'number' ? nuovoMatch.teamB : -1;

      // Ottieni i nomi dei membri tramite indici
      const teamAInfo = atletiByIndex[teamAIndex];
      const teamBInfo = atletiByIndex[teamBIndex];

      // Assegna nomi espliciti per una visualizzazione stabile
      if (teamAInfo) {
        nuovoMatch.atleta1 = teamAInfo.memberName;
        nuovoMatch.teamAName = teamAInfo.memberName;
      }

      if (teamBInfo) {
        nuovoMatch.atleta2 = teamBInfo.memberName;
        nuovoMatch.teamBName = teamBInfo.memberName;
      }

      // Supporto per la modalità mista (mantiene la compatibilità)
      const tipo = localStorage.getItem('competitionType');
      if (tipo === 'mixed') {
        if ((!nuovoMatch.squadraA || nuovoMatch.squadraA.trim() === '') && teamAIndex >= 0 && teams[teamAIndex]) {
          nuovoMatch.squadraA = teams[teamAIndex].name || '';
        }
        if ((!nuovoMatch.squadraB || nuovoMatch.squadraB.trim() === '') && teamBIndex >= 0 && teams[teamBIndex]) {
          nuovoMatch.squadraB = teams[teamBIndex].name || '';
        }
      }

      return nuovoMatch;
    });
  }, []); // Dipendenze per migraNomiSquadreMixed

  // Funzione per applicare le stoccate totali ai match
  const applyStoccateTotali = useCallback((matches, currentType, options = {}) => {
    // Recupera valori correnti con fallback su options o localStorage
    const savedIndGirone = options.stoccateGirone || localStorage.getItem('individual_stoccateGirone') || '5';
    const savedIndElim = options.stoccateElimDiretta || localStorage.getItem('individual_stoccateElimDiretta') || '15';
    const savedTeamGirone = localStorage.getItem('team_stoccateGirone') || '45';
    const savedTeamElim = localStorage.getItem('team_stoccateElimDiretta') || '45';

    return matches.map(match => {
      // Se è già definito stoccateTotali e non stiamo forzando un ricalcolo, usa quello
      if (match.stoccateTotali && !options.force) return match;

      // Altrimenti, imposta in base al tipo di competizione e tipo di match
      const isEliminationMatch = match.isEliminationMatch || false;

      let stoccateTotali;
      if (currentType === 'team') {
        stoccateTotali = isEliminationMatch ? savedTeamElim : savedTeamGirone;
      } else {
        stoccateTotali = isEliminationMatch ? savedIndElim : savedIndGirone;
      }

      return {
        ...match,
        stoccateTotali: parseInt(stoccateTotali, 10)
      };
    });
  }, []); // Dipendenze per applyStoccateTotali

  // Forza il caricamento dei dati da localStorage anche quando si ritorna su questa pagina

  useEffect(() => {
    // Ottieni dati dalla location se ci arriviamo via navigazione programmatica
    const locationState = location.state || {};

    // Carica teams e matches
    try {
      // Se abbiamo dati nella location.state, li usiamo direttamente
      if (locationState.teams && locationState.matches) {
        console.log('Carico dati dalla navigazione:', {
          teams: locationState.teams.length,
          matches: locationState.matches.length
        });

        const storedType = localStorage.getItem('competitionType') || 'team';
        setCompetitionType(storedType);

        // Usa i dati forniti direttamente
        const teamsFromNav = locationState.teams.map(t => ({
          ...t,
          members: Array.isArray(t.members) ? t.members : []
        }));

        setTeams(teamsFromNav);
        setTeamsLoaded(true);

        // Applica la migrazione dei nomi
        const migratedMatchesFromNav = migraNomiSquadreMixed(locationState.matches, teamsFromNav);

        // Applica stoccate totali anche qui!
        const stoccateGironeFromNav = locationState.stoccateGirone;
        const matchesWithStoccateFromNav = applyStoccateTotali(migratedMatchesFromNav, storedType, {
          stoccateGirone: stoccateGironeFromNav
        });

        setMatches(matchesWithStoccateFromNav.map(m => ({
          ...m,
          scoreA: sanitizeScore(m.scoreA),
          scoreB: sanitizeScore(m.scoreB)
        })));
        setMatchesLoaded(true);

        // Estrai i gironi disponibili
        // availablePools rimosso (inutilizzato)

        // Usa le stoccate dalla navigazione se disponibili
        if (locationState.stoccateGirone) {
          setStoccateGirone(parseInt(locationState.stoccateGirone));
        } else {
          const stoccateGironeValue = localStorage.getItem('individual_stoccateGirone') || localStorage.getItem('stoccatePerAssalto') || '5';
          setStoccateGirone(parseInt(stoccateGironeValue));
        }

        // Determina se è una competizione individuale
        // isIndividualCompetition rimosso (inutilizzato)

        // Carica stoccate ElimDiretta da localStorage
        const stoccateElimDirettaValue = localStorage.getItem('individual_stoccateElimDiretta') || '15';
        setStoccateElimDiretta(parseInt(stoccateElimDirettaValue));
      } else {
        // Altrimenti carica da localStorage come prima
        const storedTeams = loadTeams();
        const storedMatches = loadMatches();
        const storedType = localStorage.getItem('competitionType') || 'team';

        console.log('MatchSchedule - Dati caricati da localStorage:', {
          tipo: storedType,
          teams: storedTeams.length,
          matches: storedMatches.length
        });

        setCompetitionType(storedType);

        // Applica la migrazione dei nomi squadre per le competizioni miste
        const migratedMatches = migraNomiSquadreMixed(storedMatches, storedTeams);

        setTeams(storedTeams.map(t => ({
          ...t,
          members: Array.isArray(t.members) ? t.members : []
        })));
        setTeamsLoaded(true);
        setMatches(migratedMatches.map(m => ({
          ...m,
          scoreA: sanitizeScore(m.scoreA),
          scoreB: sanitizeScore(m.scoreB)
        })));
        setMatchesLoaded(true);

        // Carica stoccate da localStorage
        const stoccateGironeValue = localStorage.getItem('individual_stoccateGirone') || localStorage.getItem('stoccatePerAssalto') || '5';
        const stoccateElimDirettaValue = localStorage.getItem('individual_stoccateElimDiretta') || '15';

        setStoccateGirone(parseInt(stoccateGironeValue));
        setStoccateElimDiretta(parseInt(stoccateElimDirettaValue));

        // Determina se è una competizione individuale
        // isIndividualCompetition rimosso (inutilizzato)

        // Estrai i gironi disponibili
        // availablePools rimosso (inutilizzato)

        // Debug dei primi 2 match
        if (migratedMatches.length > 0) {
          console.log('Primi 2 match dopo migrazione:', migratedMatches.slice(0, 2));
          console.log('Prime 2 squadre:', storedTeams.slice(0, 2));
        }
      }
    } catch (e) {
      console.error('Errore nel caricamento dati:', e);
    }
  }, [location.state, loadInversionePreferences, migraNomiSquadreMixed, applyStoccateTotali]);

  useEffect(() => {
    console.log('Teams State:', teams);
    console.log('Matches State:', editableMatches);
  }, [teams, editableMatches]);

  useEffect(() => {
    const handleVisibility = () => {
      // Ricarica dati quando la pagina diventa visibile
      const storedTeams = JSON.parse(localStorage.getItem('teams') || '[]');
      const storedMatches = JSON.parse(localStorage.getItem('matches') || '[]');
      const storedType = localStorage.getItem('competitionType') || 'team';

      setCompetitionType(storedType);
      const migratedMatches = migraNomiSquadreMixed(storedMatches, storedTeams);

      // Forza nuovo reference array per triggerare rerender
      setTeams(JSON.parse(JSON.stringify(storedTeams)).map(t => ({ ...t })));
      setTeamsLoaded(true);
      setMatches(JSON.parse(JSON.stringify(migratedMatches)).map(m => ({ ...m })));
      setMatchesLoaded(true);
    };

    const handleVisibilityChangeEvent = () => {
      if (document.visibilityState === 'visible') {
        handleVisibility();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChangeEvent);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChangeEvent);
    };
  }, [migraNomiSquadreMixed]);

  // Funzione per popolare la lista degli atleti
  const populateAthletesList = useCallback(() => {
    if (!teams || teams.length === 0) return;

    let athletes = [];

    if (competitionType === 'individual') {
      // Per competizioni individuali, ogni team è un atleta
      athletes = teams.map(team => ({
        id: typeof team.id !== 'undefined' ? team.id : teams.indexOf(team),
        name: team.name,
        pool: team.pool
      }));
    } else {
      // Per competizioni a squadre/miste, mostra SOLO i nomi delle squadre (richiesta utente)
      athletes = teams.map(team => ({
        id: typeof team.id !== 'undefined' ? team.id : teams.indexOf(team),
        name: team.name,
        pool: team.pool
      }));
    }

    // Rimuovi duplicati (possibile in caso di omonimi)
    const uniqueAthletes = athletes.reduce((acc, current) => {
      const duplicate = acc.find(item => item.name === current.name);
      if (!duplicate) {
        acc.push(current);
      }
      return acc;
    }, []);

    // Ordina alfabeticamente (con check per nomi non stringa)
    uniqueAthletes.sort((a, b) => {
      const nameA = typeof a.name === 'string' ? a.name : String(a.name || '');
      const nameB = typeof b.name === 'string' ? b.name : String(b.name || '');
      return nameA.localeCompare(nameB);
    });

    setAthletesList(uniqueAthletes);
  }, [teams, competitionType]); // Dipendenze per populateAthletesList

  useEffect(() => {
    populateAthletesList();
  }, [populateAthletesList, teams, competitionType]);


  // Gestione della visibilità della pagina
  useEffect(() => {
    const handleVisibility = () => {
      try {
        // Carica i dati aggiornati
        const loadedTeams = JSON.parse(localStorage.getItem('teams') || '[]');
        const loadedMatches = JSON.parse(localStorage.getItem('matches') || '[]');
        const storedType = localStorage.getItem('competitionType') || 'team';

        console.log('Ricarica dati - visibilità pagina:', {
          tipo: storedType,
          teams: loadedTeams.length,
          matches: loadedMatches.length
        });

        setCompetitionType(storedType);
        // isIndividualCompetition rimosso (inutilizzato)

        // Applica migrazione nomi squadre per mixed
        const migratedMatches = migraNomiSquadreMixed(loadedMatches, loadedTeams);

        // Applica stoccate totali - Passando il tipo corretto!
        const matchesWithStoccate = applyStoccateTotali(migratedMatches, storedType);

        // Forza nuovo reference array per triggerare rerender
        setTeams(JSON.parse(JSON.stringify(loadedTeams)).map(t => ({ ...t })));
        setTeamsLoaded(true);
        setMatches(JSON.parse(JSON.stringify(matchesWithStoccate)).map(m => ({ ...m })));
        setMatchesLoaded(true);
      } catch (e) {
        console.error('Errore nel ricaricamento dati:', e);
      }
    };

    window.addEventListener('focus', handleVisibility);
    window.addEventListener('matchesUpdated', handleVisibility);

    // Esegui subito all'avvio
    handleVisibility();

    return () => {
      window.removeEventListener('focus', handleVisibility);
      window.removeEventListener('matchesUpdated', handleVisibility);
    };
  }, [stoccateGirone, stoccateElimDiretta, migraNomiSquadreMixed, applyStoccateTotali]);

  const alternatePoolsSort = useCallback((matches) => {
    const uniquePools = [...new Set(matches.map(m => m.pool))];
    const matchesByPool = {};
    uniquePools.forEach(pool => {
      matchesByPool[pool] = matches.filter(m => m.pool === pool);
    });

    const result = [];
    let currentIndex = 0;
    let hasMore = true;

    while (hasMore) {
      hasMore = false;
      for (const pool of uniquePools) {
        if (matchesByPool[pool].length > currentIndex) {
          result.push(matchesByPool[pool][currentIndex]);
          hasMore = true;
        }
      }
      currentIndex++;
    }
    return result;
  }, []); // Dipendenze per alternatePoolsSort;

  const renderPoolButtons = () => {
    const pools = [...new Set(editableMatches.map(m => m.pool))].sort();

    // Creazione separata degli elementi per evitare problemi di chiavi
    const allPoolsButton = (
      <TouchableOpacity
        key="all-pools"
        style={[
          styles.poolButton,
          filteredPool === null && styles.poolButtonActive
        ]}
        onPress={() => setFilteredPool(null)}
      >
        <Text style={filteredPool === null ? styles.poolButtonTextActive : styles.poolButtonText}>Tutti</Text>
      </TouchableOpacity>
    );

    const poolButtons = pools.map(pool => {
      // Label: se pool è numerico, mostra pool+1, altrimenti mostra il valore originale
      let label = pool;
      if (!isNaN(pool) && pool !== '' && pool !== null) {
        label = (parseInt(pool, 10) + 1).toString();
      }
      return (
        <TouchableOpacity
          key={`pool-${pool}`}
          style={[
            styles.poolButton,
            filteredPool === pool && styles.poolButtonActive
          ]}
          onPress={() => {
            setFilteredPool(pool);
          }}
        >
          <Text style={filteredPool === pool ? styles.poolButtonTextActive : styles.poolButtonText}>
            Girone {label}
          </Text>
        </TouchableOpacity>
      );
    });

    const pendingButton = (
      <TouchableOpacity
        key="pending-matches"
        style={[
          styles.poolButton,
          showIncomplete && {
            backgroundColor: '#4CAF50',
            borderColor: '#4CAF50'
          }
        ]}
        onPress={() => setShowIncomplete(!showIncomplete)}
      >
        <Text style={showIncomplete ? styles.poolButtonTextActive : styles.poolButtonText}>
          Da tirare
        </Text>
      </TouchableOpacity>
    );

    // Chip per il filtro atleta
    const athleteButton = (
      <TouchableOpacity
        key="athlete-filter"
        style={[
          styles.poolButton,
          selectedAthlete && {
            backgroundColor: '#FF9800',
            borderColor: '#FF9800'
          }
        ]}
        onPress={() => setShowAthleteModal(true)}
      >
        <View style={{ flexDirection: 'row', alignItems: 'center' }}>
          <Text style={selectedAthlete ? styles.poolButtonTextActive : styles.poolButtonText}>
            {selectedAthlete || (competitionType === 'individual' ? "Tiratore" : "Squadra")}
          </Text>
          {selectedAthlete && (
            <TouchableOpacity
              style={{ marginLeft: 8 }}
              onPress={(e) => {
                e.stopPropagation();
                setSelectedAthlete(null);
              }}
            >
              <Text style={{ color: 'white', fontWeight: 'bold', fontSize: 16 }}>×</Text>
            </TouchableOpacity>
          )}
        </View>
      </TouchableOpacity>
    );

    // Unire gli elementi con chiavi esplicite
    return (
      <View style={{
        flexDirection: 'row',
        flexWrap: 'wrap',
        justifyContent: 'center',
        marginBottom: 15,
        paddingHorizontal: 10,
        gap: 8,
      }}>
        {allPoolsButton}
        {poolButtons}
        {pendingButton}
        {athleteButton}
      </View>
    );
  };

  const styles = StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: '#f5f5f5',
      paddingTop: 20,
      paddingBottom: 0,
      paddingHorizontal: 10
    },
    modalOverlay: {
      flex: 1,
      backgroundColor: 'rgba(0,0,0,0.5)',
      justifyContent: 'center',
      alignItems: 'center',
      padding: 20
    },
    modalContent: {
      backgroundColor: 'white',
      borderRadius: 8,
      padding: 20,
      width: '100%',
      maxWidth: 400,
      alignItems: 'center',
      shadowColor: '#000',
      shadowOffset: { width: 0, height: 2 },
      shadowOpacity: 0.25,
      shadowRadius: 3.84,
      elevation: 5
    },
    modalTitle: {
      fontSize: 18,
      fontWeight: 'bold',
      marginBottom: 10,
      color: '#333'
    },
    modalDivider: {
      height: 1,
      backgroundColor: '#e0e0e0',
      width: '100%',
      marginVertical: 10
    },
    athleteItem: {
      width: '100%',
      padding: 12,
      borderBottomWidth: 1,
      borderBottomColor: '#f0f0f0'
    },
    athleteItemSelected: {
      backgroundColor: '#e3f2fd'
    },
    athleteName: {
      fontSize: 16,
      color: '#333'
    },
    athleteTeam: {
      fontSize: 14,
      color: '#666',
      marginTop: 2
    },
    athletePool: {
      fontSize: 12,
      color: '#999',
      marginTop: 2
    },
    modalCloseButton: {
      marginTop: 20,
      paddingVertical: 10,
      paddingHorizontal: 20,
      borderRadius: 4,
      backgroundColor: '#1976d2'
    },
    modalCloseButtonText: {
      color: 'white',
      fontWeight: 'bold',
      fontSize: 14
    },
    loadingContainer: {
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
      padding: 20,
    },
    loadingText: {
      fontSize: 18,
      color: theme.primary,
      marginBottom: 20,
      fontWeight: 'bold',
    },
    poolContainer: {
      marginVertical: 16,
      paddingHorizontal: 8,
      backgroundColor: '#fff',
      borderWidth: 0,
    },
    poolTitle: {
      fontSize: 16,
      fontWeight: '600',
      color: '#2d3748',
      marginBottom: 8,
      textAlign: 'center',
    },
    poolButtons: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      justifyContent: 'center',
      gap: 8,
    },
    poolButton: {
      paddingVertical: 8,
      paddingHorizontal: 16,
      borderRadius: 20,
      backgroundColor: '#e2e8f0',
      borderWidth: 1,
      borderColor: '#cbd5e0',
    },
    poolButtonActive: {
      backgroundColor: '#4299e1',
      borderColor: '#3182ce',
    },
    poolButtonText: {
      color: '#4a5568',
      fontWeight: '500',
    },
    poolButtonTextActive: {
      color: 'white',
      fontWeight: '500',
    },
    matchItem: {
      padding: 12,
      borderWidth: 2,
      borderRadius: 10,
      marginBottom: 12,
      marginHorizontal: 1,
      backgroundColor: '#fff',
      boxShadow: '0 2px 12px #0001',
      width: '99%',
      maxWidth: '99%',
      minWidth: 0,
      alignSelf: 'center',
      display: 'flex',
      flexDirection: 'column',
      boxSizing: 'border-box',
      overflow: 'hidden',
    },
    teamNameContainer: {
      width: '45%',
      minWidth: 0,
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
    },
    teamName: {
      fontWeight: 'bold',
      fontSize: 15,
      textAlign: 'center',
      whiteSpace: 'normal',
      wordBreak: 'break-all',
    },
    '@media (max-width: 600px)': {
      matchItem: {
        minWidth: 0,
        width: '99%',
        padding: 8,
        borderRadius: 8,
        marginHorizontal: 1,
        marginBottom: 8,
      },
      container: {
        padding: 0,
      },
      teamName: {
        fontSize: 13,
      }
    },
    '@media (max-width: 400px)': {
      matchItem: {
        width: '99%',
        maxWidth: '99%',
        padding: 6,
        marginHorizontal: 1,
        marginBottom: 6,
        borderWidth: 2,
      },
      container: {
        padding: 0,
      },
      teamName: {
        fontSize: 11,
      }
    },
    poolLabel: {
      borderRadius: 12,
      paddingHorizontal: 8,
      paddingVertical: 2,
      backgroundColor: '#1976d2',
      alignSelf: 'center',
      marginLeft: 'auto',
      marginRight: 'auto',
    },
    poolLabelText: {
      fontSize: 12,
      fontWeight: '500',
      color: '#fff',
    },
  });

  // Gestione eventi da tastiera per punteggi
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Verifica che ci sia un match selezionato
      if (selectedMatchIndex === null || selectedMatchIndex === undefined) return;

      // Controlla se è un tasto numerico (sia riga superiore che tastierino numerico)
      const isNumKey = (e.key >= '0' && e.key <= '9') ||
        (e.code.startsWith('Numpad') && !isNaN(parseInt(e.code.replace('Numpad', ''))));

      if (isNumKey) {
        let digit;
        // Estrai il numero sia da key che da code (per compatibilità con diversi browser/tastiere)
        if (e.key >= '0' && e.key <= '9') {
          digit = e.key;
        } else if (e.code.startsWith('Numpad')) {
          digit = e.code.replace('Numpad', '');
        }

        const numValue = parseInt(digit);

        if (!isNaN(numValue)) {
          console.log(`Input numerico rilevato: ${numValue} per squadra ${activeTeamInput}`);

          // Se è attivo l'input squadra A
          if (activeTeamInput === 'A') {
            const newMatches = [...editableMatches];
            const match = { ...newMatches[selectedMatchIndex] };
            const maxScore = match.stoccateTotali || 45;
            match.scoreA = Math.min(maxScore, numValue);
            // --- LOGICA COMPLETAMENTO AUTOMATICO ---
            if ((match.scoreA === maxScore || match.scoreB === maxScore)) {
              match.completed = true;
            } else {
              // Se nessun punteggio è al massimo, il match è in corso
              match.completed = match.scoreB === maxScore;
            }
            newMatches[selectedMatchIndex] = match;
            setMatches(newMatches.map(m => ({
              ...m,
              scoreA: sanitizeScore(m.scoreA),
              scoreB: sanitizeScore(m.scoreB)
            })));
            localStorage.setItem('matches', JSON.stringify(newMatches));
            console.log(`Punteggio squadra A impostato a ${numValue}`);
          }
          // Se è attivo l'input squadra B
          else if (activeTeamInput === 'B') {
            const newMatches = [...editableMatches];
            const match = { ...newMatches[selectedMatchIndex] };
            const maxScore = match.stoccateTotali || 45;
            match.scoreB = Math.min(maxScore, numValue);
            // --- LOGICA COMPLETAMENTO AUTOMATICO ---
            if ((match.scoreA === maxScore || match.scoreB === maxScore)) {
              match.completed = true;
            } else {
              // Se nessun punteggio è al massimo, il match è in corso
              match.completed = match.scoreA === maxScore;
            }
            newMatches[selectedMatchIndex] = match;
            setMatches(newMatches.map(m => ({
              ...m,
              scoreA: sanitizeScore(m.scoreA),
              scoreB: sanitizeScore(m.scoreB)
            })));
            localStorage.setItem('matches', JSON.stringify(newMatches));
            console.log(`Punteggio squadra B impostato a ${numValue}`);
          }
        }
      }
    };

    // Aggiungere listener per catturare gli eventi da tastiera
    document.addEventListener('keydown', handleKeyDown);

    // Clean up del listener quando il componente viene smontato
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [editableMatches, selectedMatchIndex, activeTeamInput, setMatches]);

  // Funzione per gestire l'input del numpad
  const handleNumpadInput = (digit) => {
    if (digit === 'del') {
      // Cancella l'ultima cifra
      setInputValue(inputValue.slice(0, -1));
      // Se si cancella tutto, riattivare la modalità sostituzione
      if (inputValue.length <= 1) {
        setIsInputReplaceMode(true);
      }
    } else if (digit === 'ok') {
      // Conferma input
      if (inputValue !== '') {
        const numValue = parseInt(inputValue);

        if (!isNaN(numValue) && selectedMatchIndex !== null && activeTeamInput) {
          const newMatches = [...editableMatches];
          const match = { ...newMatches[selectedMatchIndex] };
          const maxScore = parseInt(match.stoccateTotali || 45, 10);
          // Imposta il punteggio con limite massimo
          if (activeTeamInput === 'A') {
            match.scoreA = Math.min(maxScore, numValue);

            // Se il punteggio A raggiunge il massimo, il match è completato
            if (match.scoreA >= maxScore) {
              match.completed = true;
            } else {
              // Se nessun punteggio è al massimo, il match è in corso
              match.completed = match.scoreB >= maxScore;
            }
          } else if (activeTeamInput === 'B') {
            match.scoreB = Math.min(maxScore, numValue);

            // Se il punteggio B raggiunge il massimo, il match è completato
            if (match.scoreB >= maxScore) {
              match.completed = true;
            } else {
              // Se nessun punteggio è al massimo, il match è in corso
              match.completed = match.scoreA >= maxScore;
            }
          }

          // Non permettere che entrambi abbiano il punteggio massimo contemporaneamente
          if (match.scoreA >= maxScore && match.scoreB >= maxScore) {
            if (activeTeamInput === 'A') {
              match.scoreB = maxScore - 1;
            } else {
              match.scoreA = maxScore - 1;
            }
            match.completed = true;
          }

          newMatches[selectedMatchIndex] = match;
          setMatches(newMatches.map(m => ({
            ...m,
            scoreA: sanitizeScore(m.scoreA),
            scoreB: sanitizeScore(m.scoreB)
          })));
          localStorage.setItem('matches', JSON.stringify(newMatches));
        }
      }

      // Chiudi il numpad e resetta l'input
      setShowNumpad(false);
      setInputValue('');

      // Reset della modalità input
      setIsInputReplaceMode(true);

      // Forza aggiornamento caricando i dati aggiornati
      const updatedMatches = JSON.parse(localStorage.getItem('matches') || '[]');
      setMatches(updatedMatches.map(m => ({
        ...m,
        scoreA: sanitizeScore(m.scoreA),
        scoreB: sanitizeScore(m.scoreB)
      })));
    } else if (inputValue.length < 2) {
      // Se è in modalità di sostituzione, sostituisci completamente
      if (isInputReplaceMode) {
        setInputValue(digit);
        // Disattiva la modalità di sostituzione dopo il primo input
        setIsInputReplaceMode(false);
      } else {
        // Altrimenti aggiungi la cifra (max 2 cifre)
        setInputValue(inputValue + digit);
      }
    }
  };

  // handleScoreTouch rimossa (inutilizzata)


  // Passa solo l'id (indice) del match a ScoreKeeper per evitare errori di indice
  const handleMatchPress = (match, index) => {
    // Usa l'ID del match dall'oggetto match invece dell'indice nella lista filtrata
    const actualMatchId = editableMatches.findIndex(m => m.id === match.id);


    // IMPORTANTE: Usa l'ID effettivo del match, con fallback sull'indice se undefined
    // Questo garantisce coerenza con le chiavi di inversione salvate
    const effectiveMatchId = match.id !== undefined ? match.id : index;

    console.log(`[MatchSchedule] handleMatchPress: actualMatchId=${actualMatchId}, effectiveMatchId=${effectiveMatchId}`);
    console.log('[MatchSchedule] Match Object:', JSON.stringify(match, null, 2));

    // Determina se è un match di eliminazione diretta
    // I match di girone hanno la proprietà 'pool' definita
    // I match di eliminazione diretta hanno 'isEliminationMatch', 'round' definito o 'pool' undefined
    // IMPORTANTE: match.isEliminationMatch è la proprietà più affidabile perché usata anche per il calcolo stoccate nel render
    const isBracketMatch = match.isEliminationMatch === true || match.round !== undefined || match.pool === undefined;

    console.log(`[MatchSchedule] isBracketMatch detection: isElim=${match.isEliminationMatch}, round=${match.round}, pool=${match.pool} -> ${isBracketMatch}`);

    // Passa matchRealId come stringa per coerenza con localStorage
    navigate('/score', {
      state: {
        matchId: actualMatchId,
        matchRealId: String(effectiveMatchId),
        bracketMode: isBracketMatch,
        from: 'schedule'
      }
    });
  };

  const renderMatch = ({ item, index }) => {
    // Rimuovo log eccessivi di debug in produzione
    // console.log('[RENDER DEBUG] Match:', item);
    // console.log('[RENDER DEBUG] TeamA Index:', item.teamA, 'TeamA:', teams[item.teamA]);
    // console.log('[RENDER DEBUG] TeamB Index:', item.teamB, 'TeamB:', teams[item.teamB]);

    // DICHIARAZIONE VARIABILI NOMI SQUADRE
    let teamA = '';
    let teamB = '';

    // Assicuriamoci che il match abbia un ID valido
    const matchId = item.id || index;

    // Verifica se il match ha una proprietà id valida
    if (item.id === undefined) {
      console.warn(`[MATCH WARNING] Match senza ID all'indice ${index}, usiamo l'indice come fallback`);
      // Aggiunta automatica dell'ID se mancante
      item.id = index;
    }

    // Controlla se questo match ha l'inversione lati attiva
    const isMatchInverted = getMatchInversion(matchId);
    console.log(`[DEBUG INVERSIONE] Match ID: ${matchId}, Invertito: ${isMatchInverted}`, matchInversions);

    // normalizeName rimossa (inutilizzata)

    // Logica isolata per diversi tipi di competizione (come richiesto dalla memoria 069357e1)
    const tipo = localStorage.getItem('competitionType');

    // Gestione speciale per le gare individuali
    if (tipo === 'individual') {
      // Per le gare individuali, il nome del tiratore è direttamente nel campo della squadra
      if (typeof item.teamA === 'number' && teams[item.teamA]) {
        teamA = teams[item.teamA].name;
      }

      if (typeof item.teamB === 'number' && teams[item.teamB]) {
        teamB = teams[item.teamB].name;
      }
    } else if (tipo === 'mixed') {
      // Logica specifica per le gare miste (come richiesto dalla memoria 6b744af8)
      // Usa direttamente squadraA/squadraB se presenti
      if (item.squadraA) {
        teamA = item.squadraA;
      } else if (typeof item.teamA === 'number' && teams[item.teamA]) {
        teamA = teams[item.teamA].name;
      } else if (typeof item.teamA === 'string') {
        const t = teams.find(t => t.name === item.teamA);
        if (t) teamA = t.name;
      } else if (item.team1 && item.team1.name) {
        teamA = item.team1.name;
      }

      if (item.squadraB) {
        teamB = item.squadraB;
      } else if (typeof item.teamB === 'number' && teams[item.teamB]) {
        teamB = teams[item.teamB].name;
      } else if (typeof item.teamB === 'string') {
        const t = teams.find(t => t.name === item.teamB);
        if (t) teamB = t.name;
      } else if (item.team2 && item.team2.name) {
        teamB = item.team2.name;
      }

      if (!teamA) teamA = '(Nome mancante)';
      if (!teamB) teamB = '(Nome mancante)';
      console.log('[DEBUG PATCH] item.teamA:', item.teamA, 'item.teamB:', item.teamB, '-> teamA:', teamA, 'teamB:', teamB);
    } else {
      // Logica per le gare a squadre
      if (typeof item.teamA === 'number' && teams[item.teamA]) {
        teamA = teams[item.teamA].name;
      } else if (item.squadraA) {
        teamA = item.squadraA;
      } else if (item.team1 && typeof item.team1 === 'object' && item.team1.name) {
        teamA = item.team1.name;
      }

      if (typeof item.teamB === 'number' && teams[item.teamB]) {
        teamB = teams[item.teamB].name;
      } else if (item.squadraB) {
        teamB = item.squadraB;
      } else if (item.team2 && typeof item.team2 === 'object' && item.team2.name) {
        teamB = item.team2.name;
      }
    }

    // Fallback se ancora non abbiamo trovato i nomi
    if (!teamA) teamA = '(Nome mancante)';
    if (!teamB) teamB = '(Nome mancante)';

    // hasAssalti rimosso (inutilizzato)

    // Stato e selezione per il match
    const status = getMatchStatus(item);
    const isSelected = selectedMatchIndex === index;

    // Accedi ai dati visualizzati usando getTeamName
    // Per mixed mode, prioritizza squadraA/squadraB che sono già stringhe
    // (nota: 'tipo' è già definito sopra alla linea 1160)
    let rawTeamNameA, rawTeamNameB;

    if (tipo === 'mixed') {
      // Per mixed mode, usa direttamente squadraA/squadraB o teamA/teamB se sono stringhe
      rawTeamNameA = item.squadraA || (typeof item.teamA === 'string' ? item.teamA : null) ||
        getTeamName(teams[item.teamA], competitionType, teams[item.teamB]) || 'N/D';
      rawTeamNameB = item.squadraB || (typeof item.teamB === 'string' ? item.teamB : null) ||
        getTeamName(teams[item.teamB], competitionType, teams[item.teamA]) || 'N/D';
    } else {
      rawTeamNameA = getTeamName(teams[item.teamA], competitionType, teams[item.teamB]) || item.squadraA || 'Squadra A';
      rawTeamNameB = getTeamName(teams[item.teamB], competitionType, teams[item.teamA]) || item.squadraB || 'Squadra B';
    }

    // Applica l'inversione lati se necessario
    const teamNameA = isMatchInverted ? rawTeamNameB : rawTeamNameA;
    const teamNameB = isMatchInverted ? rawTeamNameA : rawTeamNameB;

    // Anche i punteggi devono essere invertiti
    const visualScoreA = isMatchInverted ? (item.scoreB ?? 0) : (item.scoreA ?? 0);
    const visualScoreB = isMatchInverted ? (item.scoreA ?? 0) : (item.scoreB ?? 0);

    return (
      <TouchableOpacity
        activeOpacity={0.88}
        onPress={() => {
          setSelectedMatchIndex(index);
          setActiveTeamInput(null);
          handleMatchPress(item, index);
        }}
      >
        <View
          style={{
            ...(styles.matchItem || {}),
            borderColor: isSelected ? '#ff9800' : (status?.color || theme.primary),
            backgroundColor:
              item.completed && ((item.scoreA || 0) === (item.scoreB || 0) || (item.scoreA || 0) === (item.stoccateTotali || 45) || (item.scoreB || 0) === (item.stoccateTotali || 45))
                ? '#e3f9e5'
                : item.completed
                  ? '#f2f2f2'
                  : '#fff'
          }}
        >
          <View style={{ flexDirection: 'row', alignItems: 'center', marginBottom: 6 }}>
            <View style={{ flex: 1, flexDirection: 'row', alignItems: 'center', justifyContent: 'center' }}>
              <View style={{
                backgroundColor: theme.danger,
                borderRadius: 16,
                paddingVertical: 6,
                paddingHorizontal: 10,
                alignSelf: 'center',
                marginLeft: 0,
                marginRight: 5,
                minWidth: 0,
                flex: 1,
              }}>
                <View style={{
                  flex: 1,
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minWidth: 0
                }}>
                  <Text
                    style={{
                      fontSize: 18,
                      fontWeight: 'bold',
                      color: '#fff',
                      maxWidth: '100%',
                      textAlign: 'center',
                      whiteSpace: 'normal',
                      wordBreak: 'break-all',
                      lineHeight: 20
                    }}
                    adjustsFontSizeToFit={true}
                    minimumFontScale={0.5}
                    numberOfLines={2}
                  >
                    {teamNameA}
                  </Text>
                </View>
              </View>
              <Text style={{ fontSize: 16, color: '#888', fontWeight: 'bold', marginHorizontal: 4 }}>vs</Text>
              <View style={{
                backgroundColor: theme.secondary,
                borderRadius: 16,
                paddingVertical: 6,
                paddingHorizontal: 10,
                alignSelf: 'center',
                marginLeft: 5,
                marginRight: 0,
                minWidth: 0,
                flex: 1,
              }}>
                <View style={{
                  flex: 1,
                  flexDirection: 'row',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minWidth: 0
                }}>
                  <Text
                    style={{
                      fontSize: 18,
                      fontWeight: 'bold',
                      color: '#fff',
                      maxWidth: '100%',
                      textAlign: 'center',
                      whiteSpace: 'normal',
                      wordBreak: 'break-all',
                      lineHeight: 20
                    }}
                    adjustsFontSizeToFit={true}
                    minimumFontScale={0.5}
                    numberOfLines={2}
                  >
                    {teamNameB}
                  </Text>
                </View>
              </View>
            </View>
          </View>
          <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'center', marginTop: 8 }}>
            <View style={{
              backgroundColor: '#fff',
              borderRadius: 12,
              paddingVertical: 12,
              paddingHorizontal: 20,
              minWidth: 0,
              flex: 1,
              alignItems: 'center'
            }}>
              {/* Punteggi clickabili separati */}
              <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
                {/* Pulsante - squadra A */}
                <TouchableOpacity
                  style={{
                    width: 38,
                    height: 38,
                    borderRadius: 8,
                    backgroundColor: '#ffeaea',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginRight: 4,
                    borderWidth: 1,
                    borderColor: theme.danger
                  }}
                  onPress={() => {
                    const actualMatchIndex = editableMatches.findIndex(m => m.id === item.id);
                    // Usa la squadra corretta in base all'inversione
                    const teamToUpdate = isMatchInverted ? 'B' : 'A';
                    handleQuickScore(editableMatches, setMatches, actualMatchIndex, teamToUpdate, -1);
                  }}
                  accessibilityLabel="Diminuisci punteggio squadra rossa"
                >
                  <Text style={{ color: theme.danger, fontSize: 24, fontWeight: 'bold' }}>-</Text>
                </TouchableOpacity>
                {/* Punteggio squadra A */}
                <TouchableOpacity
                  style={{
                    backgroundColor: '#f8f0f0',
                    paddingVertical: 8,
                    paddingHorizontal: 16,
                    borderRadius: 8,
                    borderWidth: 2,
                    borderColor: activeTeamInput === 'A' && selectedMatchIndex === index ? '#ff9800' : theme.danger,
                    minWidth: 60,
                    alignItems: 'center'
                  }}
                  onPress={() => {
                    // Trova l'indice reale del match nell'array editableMatches
                    const actualMatchIndex = editableMatches.findIndex(m => m.id === item.id);

                    // Gestisce il tocco sul punteggio della squadra A
                    setSelectedMatchIndex(actualMatchIndex);
                    // Imposta il team corretto in base all'inversione
                    const teamToSelect = isMatchInverted ? 'B' : 'A';
                    setActiveTeamInput(teamToSelect);

                    // Pre-imposta il valore attuale
                    const match = editableMatches[actualMatchIndex];
                    const scoreKey = isMatchInverted ? 'scoreB' : 'scoreA';

                    if (match && match[scoreKey] !== undefined) {
                      setInputValue(match[scoreKey].toString());
                    } else {
                      setInputValue('');
                    }

                    // Mostra il tastierino numerico
                    console.log('[DEBUG] Apertura tastierino per squadra', teamToSelect);
                    setShowNumpad(true);
                    setIsInputReplaceMode(true);
                  }}
                >
                  <Text style={{
                    color: theme.danger,
                    fontWeight: 'bold',
                    fontSize: 24
                  }}>
                    {sanitizeScore(visualScoreA)}
                  </Text>
                </TouchableOpacity>
                {/* Pulsante + squadra A */}
                <TouchableOpacity
                  style={{
                    width: 38,
                    height: 38,
                    borderRadius: 8,
                    backgroundColor: '#ffeaea',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginLeft: 4,
                    borderWidth: 1,
                    borderColor: theme.danger
                  }}
                  onPress={() => {
                    const actualMatchIndex = editableMatches.findIndex(m => m.id === item.id);
                    // Usa la squadra corretta in base all'inversione
                    const teamToUpdate = isMatchInverted ? 'B' : 'A';
                    handleQuickScore(editableMatches, setMatches, actualMatchIndex, teamToUpdate, 1);
                  }}
                  accessibilityLabel="Aumenta punteggio squadra rossa"
                >
                  <Text style={{ color: theme.danger, fontSize: 24, fontWeight: 'bold' }}>+</Text>
                </TouchableOpacity>

                <View style={{ marginHorizontal: 10 }}>
                  <InvertiLatiButton
                    invertiLati={isMatchInverted}
                    toggleInvertiLati={() => {
                      const effectiveId = item.id || index;
                      console.log(`[MatchSchedule] Pulsante inversione premuto per match ID ${effectiveId}, stato corrente: ${isMatchInverted}`);
                      // Questa funzione è cruciale per il corretto funzionamento
                      toggleMatchInversion(effectiveId);
                      // Forza l'aggiornamento della vista anche dopo il click
                      setTimeout(() => setMatches([...editableMatches]), 10);
                    }}
                    style={{
                      backgroundColor: 'rgba(25, 118, 210, 0.9)',
                      padding: 6,
                      borderRadius: 20,
                      width: 35,
                      height: 35,
                      alignItems: 'center',
                      justifyContent: 'center',
                      elevation: 3,
                      shadowColor: '#000',
                      shadowOffset: { width: 0, height: 1 },
                      shadowOpacity: 0.2,
                      shadowRadius: 2
                    }}
                  />
                </View>

                {/* Pulsante - squadra B */}
                <TouchableOpacity
                  style={{
                    width: 38,
                    height: 38,
                    borderRadius: 8,
                    backgroundColor: '#eaffea',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginRight: 4,
                    borderWidth: 1,
                    borderColor: theme.secondary
                  }}
                  onPress={() => {
                    const actualMatchIndex = editableMatches.findIndex(m => m.id === item.id);
                    // Usa la squadra corretta in base all'inversione
                    const teamToUpdate = isMatchInverted ? 'A' : 'B';
                    handleQuickScore(editableMatches, setMatches, actualMatchIndex, teamToUpdate, -1);
                  }}
                  accessibilityLabel="Diminuisci punteggio squadra verde"
                >
                  <Text style={{ color: theme.secondary, fontSize: 24, fontWeight: 'bold' }}>-</Text>
                </TouchableOpacity>
                {/* Punteggio squadra B */}
                <TouchableOpacity
                  style={{
                    backgroundColor: '#f0f8f0',
                    paddingVertical: 8,
                    paddingHorizontal: 16,
                    borderRadius: 8,
                    borderWidth: 2,
                    borderColor: activeTeamInput === 'B' && selectedMatchIndex === index ? '#ff9800' : theme.secondary,
                    minWidth: 60,
                    alignItems: 'center'
                  }}
                  onPress={() => {
                    // Trova l'indice reale del match nell'array editableMatches
                    const actualMatchIndex = editableMatches.findIndex(m => m.id === item.id);

                    // Gestisce il tocco sul punteggio della squadra B
                    setSelectedMatchIndex(actualMatchIndex);
                    // Imposta il team corretto in base all'inversione
                    const teamToSelect = isMatchInverted ? 'A' : 'B';
                    setActiveTeamInput(teamToSelect);

                    // Pre-imposta il valore attuale
                    const match = editableMatches[actualMatchIndex];
                    const scoreKey = isMatchInverted ? 'scoreA' : 'scoreB';

                    if (match && match[scoreKey] !== undefined) {
                      setInputValue(match[scoreKey].toString());
                    } else {
                      setInputValue('');
                    }

                    // Mostra il tastierino numerico
                    console.log('[DEBUG] Apertura tastierino per squadra', teamToSelect);
                    setShowNumpad(true);
                    setIsInputReplaceMode(true);
                  }}
                >
                  <Text style={{
                    color: theme.secondary,
                    fontWeight: 'bold',
                    fontSize: 24
                  }}>
                    {sanitizeScore(visualScoreB)}
                  </Text>
                </TouchableOpacity>
                {/* Pulsante + squadra B */}
                <TouchableOpacity
                  style={{
                    width: 38,
                    height: 38,
                    borderRadius: 8,
                    backgroundColor: '#eaffea',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginLeft: 4,
                    borderWidth: 1,
                    borderColor: theme.secondary
                  }}
                  onPress={() => {
                    const actualMatchIndex = editableMatches.findIndex(m => m.id === item.id);
                    // Usa la squadra corretta in base all'inversione
                    const teamToUpdate = isMatchInverted ? 'A' : 'B';
                    handleQuickScore(editableMatches, setMatches, actualMatchIndex, teamToUpdate, 1);
                  }}
                  accessibilityLabel="Aumenta punteggio squadra verde"
                >
                  <Text style={{ color: theme.secondary, fontSize: 24, fontWeight: 'bold' }}>+</Text>
                </TouchableOpacity>
              </View>
            </View>
          </View>
          <View style={{ flexDirection: 'column', marginTop: 8 }}>
            {/* Riga superiore - elementi allineati ai bordi */}
            <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
              {/* Chip 1: Informazioni Stoccate */}
              <View style={{
                backgroundColor: status && status.color ? status.color : theme.primary,
                borderRadius: 12,
                paddingVertical: 2,
                paddingHorizontal: 8,
                alignSelf: 'flex-start',
                marginBottom: 2,
                marginTop: 2,
                marginRight: 8,
                minWidth: 0,
                flexDirection: 'row',
                alignItems: 'center',
              }}>
                <Text style={{ color: '#fff', fontWeight: 'bold', fontSize: 12, letterSpacing: 0.2 }}>
                  S: {item.stoccatePerAssalto || '-'} / Tot: {item.stoccateTotali || '-'}
                </Text>
              </View>

              {/* Chip 2: Girone */}
              <View style={{
                backgroundColor: poolColors[item.pool] || theme.primary,
                borderRadius: 12,
                paddingVertical: 2,
                paddingHorizontal: 8,
                alignSelf: 'center',
                marginRight: 8,
                minWidth: 0,
                flexDirection: 'row',
                alignItems: 'center',
              }}>
                <Text style={{ color: '#fff', fontWeight: 'bold', fontSize: 12, letterSpacing: 0.2 }}>
                  Girone {!isNaN(item.pool) && item.pool !== '' && item.pool !== null ?
                    (parseInt(item.pool, 10) + 1).toString() :
                    item.pool || '-'}
                </Text>
              </View>

              {/* Chip 3: Stato */}
              <View style={{
                backgroundColor: status?.color || theme.primary,
                borderRadius: 12,
                paddingVertical: 2,
                paddingHorizontal: 8,
                alignSelf: 'flex-end',
                marginBottom: 2,
                marginTop: 2,
                minWidth: 0,
                flexDirection: 'row',
                alignItems: 'center',
              }}>
                <Text style={{ color: '#fff', fontWeight: 'bold', fontSize: 12, letterSpacing: 0.2 }}>
                  {status?.label || 'In programma'}
                </Text>
              </View>
            </View>
          </View>
        </View>
      </TouchableOpacity>
    );
  };

  const safeGetTeam = (index) => {
    const numericIndex = Number(index) || 0;
    return teams[numericIndex] || { name: 'N/D', members: [] };
  };

  const getTeamName = (team, competitionType) => {
    if (!team || !team.name) {
      console.log('[DEBUG] Team non valido:', team);
      return 'N/D';
    }
    const members = (team.members || []).map(m => typeof m === 'object' ? m.name : String(m));
    if (competitionType === 'individual') {
      return String(members[0] || team.name || 'N/D');
    }
    if (competitionType === 'mixed') {
      // Mostra sempre il nome della squadra, anche se uguale
      return team.name || 'N/D';
    }
    return String(team.name || 'N/D');
  };


  // Se non ci sono match validi, mostra messaggio
  if (!teamsLoaded || !matchesLoaded) {
    return (
      <View>
        <Text>Caricamento dati...</Text>
      </View>
    );
  }

  // Filtra i match in base al girone selezionato, allo stato di completamento e all'atleta selezionato
  let filteredMatches = editableMatches;

  // Filtra per girone
  if (filteredPool !== null) {
    filteredMatches = filteredMatches.filter(m => m.pool === filteredPool);
  }

  // Nuova variabile per il calcolo del progresso (rispetta solo il girone, ignora i filtri successivi)
  const progressMatches = [...filteredMatches];

  // Filtra per completamento
  if (showIncomplete) {
    filteredMatches = filteredMatches.filter(m => !m.completed);
  }

  // Filtra per atleta selezionato
  if (selectedAthlete) {
    filteredMatches = filteredMatches.filter(m => {
      // Recuperare i nomi degli atleti per questo match
      const teamA = safeGetTeam(m.teamA);
      const teamB = safeGetTeam(m.teamB);

      const nameA = getTeamName(teamA, competitionType);
      const nameB = getTeamName(teamB, competitionType);

      // Per tutte le competizioni, controlla direttamente i nomi (squadre o atleti)
      // Nota: getTeamName restituisce il nome atleta per 'individual' e il nome squadra per 'team'/'mixed'
      return nameA === selectedAthlete || nameB === selectedAthlete;
    });
  }

  // Se mostro tutti i gironi, alternali
  if (!filteredPool) {
    filteredMatches = alternatePoolsSort(filteredMatches);
  }

  console.log('[DEBUG] Pool originale:', [...new Set(editableMatches.map(m => m.pool))]);
  console.log('[DEBUG] Filtro attivo:', filteredPool);
  console.log('[DEBUG] Matches filtrati:',
    filteredPool !== null
      ? editableMatches.filter(m => m.pool === filteredPool)
      : 'Tutti'
  );

  // Mostra un indicatore di caricamento se i dati non sono ancora pronti
  if (!teamsLoaded || !matchesLoaded) {
    return (
      <View style={styles.container}>
        <View style={styles.loadingContainer}>
          <Text style={styles.loadingText}>Caricamento degli incontri in corso...</Text>
        </View>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      {/* Header con titolo */}
      <View style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 20
      }}>
        <View style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', marginTop: 40, marginBottom: 16 }}>
          <View style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
            {/* Icona schedule */}
            <ScheduleIcon style={{ fontSize: 36, color: theme.primary, marginRight: 12 }} />
            {/* Titolo */}
            <Text style={{ fontSize: 32, fontWeight: 'bold', color: theme.primary, margin: 0, textAlign: 'center', letterSpacing: 1, textShadow: '0px 2px 4px rgba(0,0,0,0.06)' }}>
              Assalti
            </Text>
          </View>
          {/* Separatore blu */}
          <View style={{ width: 72, height: 4, backgroundColor: theme.primary, borderRadius: 2, marginTop: 2, marginBottom: 18, opacity: 0.2 }} />
        </View>

      </View>

      {/* Filtro gironi */}
      {renderPoolButtons()}

      {/* Status */}
      <View style={{
        marginBottom: 12,
        display: 'flex',
        justifyContent: 'center'
      }}>
        <View style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          padding: 16,
          paddingTop: 8,
          paddingBottom: 8,
          backgroundColor: '#f0f4f8',
          borderRadius: 8,
          boxShadow: '0px 2px 4px rgba(0,0,0,0.1)',
        }}>
          <Text style={{
            fontWeight: 'bold',
            marginBottom: 4,
            color: theme.primary
          }}>
            <Text>{progressMatches.filter(m => m.completed).length} / {progressMatches.length} incontri completati</Text>
          </Text>
          <View style={{
            height: 8,
            width: '100%',
            backgroundColor: '#e0e0e0',
            borderRadius: 4,
            overflow: 'hidden'
          }}>
            <View style={{
              height: '100%',
              width: `${(progressMatches.filter(m => m.completed).length / Math.max(1, progressMatches.length)) * 100}%`,
              backgroundColor: theme.secondary,
              borderRadius: 4
            }} />
          </View>
        </View>
      </View>

      {/* Lista match */}
      {filteredMatches.length === 0 ? (
        <View style={{
          textAlign: 'center',
          padding: 20,
          backgroundColor: 'white',
          borderRadius: 8,
          marginTop: 20,
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
          <Text style={{ fontSize: 16, color: '#666' }}>Nessun incontro disponibile.</Text>
        </View>
      ) : (
        <FlatList
          data={filteredMatches}
          renderItem={renderMatch}
          keyExtractor={(item, index) => item.id || index.toString()}
          extraData={{ teams, editableMatches, filteredPool }}
          contentContainerStyle={{ paddingBottom: 0 }}
        />
      )}

      {/* MODALE Tastierino numerico custom */}
      <View style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: 0, marginBottom: 0 }}>
        <TouchableOpacity
          onPress={() => navigate('/table')}
          style={{
            width: '100%',
            backgroundColor: '#ff9800',
            borderRadius: 24,
            paddingVertical: 14,
            paddingHorizontal: 24,
            boxShadow: '0px 4px 8px rgba(0,0,0,0.2)',
            alignSelf: 'center'
          }}
        >
          <Text style={{ color: '#fff', fontWeight: 'bold', fontSize: 17, letterSpacing: 0.5, textAlign: 'center' }}>Classifica dopo girone</Text>
        </TouchableOpacity>
      </View>
      <ScrollToTopButton />

      {/* Utilizziamo il nuovo componente NumpadModal */}
      <NumpadModal
        visible={showNumpad}
        onClose={() => setShowNumpad(false)}
        onNumberPress={handleNumpadInput}
        activeTeamInput={activeTeamInput}
        inputValue={inputValue}
      />

      {/* Modal per la selezione dell'atleta */}
      <Modal
        visible={showAthleteModal}
        transparent={true}
        animationType="fade"
        onRequestClose={() => setShowAthleteModal(false)}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContent}>
            <Text style={styles.modalTitle}>
              {competitionType === 'individual' ? 'Seleziona Tiratore' : 'Seleziona Squadra'}
            </Text>
            <View style={styles.modalDivider} />

            <ScrollView style={{ width: '100%', maxHeight: 400 }}>
              {/* Opzione per rimuovere il filtro */}
              <TouchableOpacity
                style={styles.athleteItem}
                onPress={() => {
                  setSelectedAthlete(null);
                  setShowAthleteModal(false);
                }}
              >
                <Text style={[styles.athleteName, { color: '#1976d2', fontWeight: 'bold' }]}>Mostra tutti</Text>
              </TouchableOpacity>

              {/* Filtra atleti per girone corrente se un girone è selezionato */}
              {athletesList
                .filter(athlete => filteredPool === null || athlete.pool === filteredPool)
                .map((athlete, index) => (
                  <TouchableOpacity
                    key={athlete.id || index}
                    style={[styles.athleteItem, selectedAthlete === athlete.name && styles.athleteItemSelected]}
                    onPress={() => {
                      setSelectedAthlete(athlete.name);
                      setShowAthleteModal(false);
                    }}
                  >
                    <Text style={styles.athleteName}>{athlete.name}</Text>
                    <Text style={styles.athletePool}>Girone {typeof athlete.pool === 'number' ? athlete.pool + 1 : athlete.pool}</Text>
                  </TouchableOpacity>
                ))}
            </ScrollView>

            <TouchableOpacity
              style={styles.modalCloseButton}
              onPress={() => setShowAthleteModal(false)}
            >
              <Text style={styles.modalCloseButtonText}>Chiudi</Text>
            </TouchableOpacity>
          </View>
        </View>
      </Modal>

      {/* Log dello stato del tastierino per debug */}
      {showNumpad && console.log('[DEBUG] Tastierino visibile con:', { activeTeamInput, inputValue })}
    </View>
  );
};

export default MatchSchedule;
